/**
 * Client-side smart square crop for the gallery wall.
 *
 * Lets the wall show a correctly-cropped preview the instant a photo is picked
 * (no server round-trip, no re-crop "jump"). The SAME region is sent to
 * /api/wall/upload so Cloudinary c_crop produces a matching print — what you see
 * is what prints. Reuses the app's existing face detection (already bundled for
 * EditImage), so no new dependency.
 */
import { detectFaces } from "./faceDetection";

// Synchronous, instant fallback: square centered horizontally, biased to the
// upper third (faces usually sit high). Region is in ORIGINAL pixels.
export function centerSquare(w, h) {
  const size = Math.min(w, h);
  return { x: Math.round((w - size) / 2), y: Math.round((h - size) * 0.33), size };
}

// Face-aware square (async). `source` may be a DOWNSCALED canvas/img (to bound
// memory on iOS) — the detected box is in the source's pixel space, so we map it
// back to the ORIGINAL (w x h) pixels. Falls back to centerSquare if no faces / not ready.
export async function faceSquare(source, w, h) {
  const size = Math.min(w, h);
  try {
    const fd = await detectFaces(source);
    if (fd && fd.boundingBox) {
      const sw = (source && (source.width || source.naturalWidth)) || w;
      const sh = (source && (source.height || source.naturalHeight)) || h;
      const rx = w / sw;
      const ry = h / sh;
      const { minX, minY, maxX, maxY } = fd.boundingBox;
      const cx = ((minX + maxX) / 2) * rx;
      const cy = ((minY + maxY) / 2) * ry;
      let x = Math.round(cx - size / 2);
      let y = Math.round(cy - size / 2);
      x = Math.max(0, Math.min(w - size, x));
      y = Math.max(0, Math.min(h - size, y));
      return { x, y, size };
    }
  } catch (e) {
    /* model not ready / error -> center */
  }
  return centerSquare(w, h);
}

// CSS object-position (for object-fit:cover on a square container) that shows
// exactly `region` of the original. Only the overflowing axis is positioned.
export function objectPosition(region, w, h) {
  const { x, y, size } = region;
  const px = w > size ? (x / (w - size)) * 100 : 50;
  const py = h > size ? (y / (h - size)) * 100 : 50;
  return `${px}% ${py}%`;
}

// Warm up the face model so the first upload's refine is fast (avoids a visible
// center->face shift). Fire-and-forget on wall mount.
let _preloaded = false;
export function preloadFaceModel() {
  if (_preloaded) return;
  _preloaded = true;
  try {
    const c = document.createElement("canvas");
    c.width = 8;
    c.height = 8;
    detectFaces(c).catch(() => {});
  } catch (e) {
    /* ignore */
  }
}
